import { HttpClient, HttpErrorResponse } from '@angular/common/http'

/**
 * Gets authentication token from sidenav
 */
export const getToken = (): Promise<string> => {
  const getTokenFn = (
    window as unknown as {
      sidenav?: { getAccessTokenSilentlyOrLogout?: () => Promise<string> }
    }
  )?.sidenav?.getAccessTokenSilentlyOrLogout
  if (!getTokenFn) {
    return Promise.reject(new Error('Cannot find SidenavExports'))
  }
  return getTokenFn()
}

/**
 * Response type for presigned URL API
 */
export interface PresignedUrlResponse {
  url: string
}

/**
 * Downloads a file using the presigned URL approach
 */
export const downloadGeneratedFile = async (
  httpClient: HttpClient,
  {
    filename,
    originalSource,
    onError,
  }: {
    filename: string
    originalSource?: string
    onError?: (message: string) => void
  }
): Promise<void> => {
  try {
    // For non-gpt-ws URLs, open directly
    if (!filename.includes('gpt-ws')) {
      window.open(filename, '_blank')
      return
    }

    // Get the presigned URL
    const presignedUrlResponse = await getPresignedUrl(httpClient, filename)

    // Open the presigned URL in a new tab
    if (presignedUrlResponse && presignedUrlResponse.url) {
      window.open(presignedUrlResponse.url, '_blank')
      return
    }

    // Fallback to original source or filename if no presigned URL
    handleFallback(originalSource, filename)
  } catch (error) {
    console.error('Error downloading the file:', (error as Error).message)
    
    // Handle specific error cases
    if (error instanceof HttpErrorResponse) {
      handleErrorCases({
        error,
        filename,
        originalSource,
        onError,
      })
      return
    }

    // For other errors, use fallback
    if (onError && error instanceof Error) onError(error.message)
    handleFallback(originalSource, filename)
  }
}

/**
 * Helper function to open a URL in a new tab
 */
export const openInNewTab = (url: string) => window.open(url, '_blank')

/**
 * Helper function to handle fallback to original source or filename
 */
export const handleFallback = (
  originalSource: string | undefined,
  filename: string
) => {
  if (originalSource) {
    openInNewTab(originalSource)
  } else {
    openInNewTab(filename)
  }
}

/**
 * Gets a presigned URL from the backend
 */
export const getPresignedUrl = async (
  httpClient: HttpClient,
  filename: string
): Promise<PresignedUrlResponse | null> => {
  try {
    // Add direct_url=true parameter to get the presigned URL directly
    const urlObj = new URL(filename)
    urlObj.searchParams.append('direct_url', 'true')
    const urlWithParam = urlObj.toString()
    
    const token = await getToken()
    const response = await httpClient
      .get<PresignedUrlResponse>(urlWithParam, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .toPromise()

    return response || null
  } catch (error) {
    console.error('Error getting presigned URL:', error)
    throw error
  }
}

/**
 * Helper function to handle various error cases
 */
export const handleErrorCases = ({
  error,
  filename,
  originalSource,
  onError,
}: {
  error: HttpErrorResponse
  filename: string
  originalSource?: string
  onError?: (message: string) => void
}) => {
  // Extract error message
  const errorMessage = extractErrorMessage(error)
  const status = error.status

  // Case 1: Special error message that should fall back to original URL where gpt-ws document endpoint is not available
  if (errorMessage === 'Unable to find matching target resource method' || 
      (error.error && typeof error.error === 'object' && 'errorId' in error.error)) {
    console.log('Resource not found, falling back to original URL')
    handleFallback(originalSource, filename)
    return
  }

  // Case 2: File not found error - show toast message but don't open URL
  if (
    status === 404 ||
    (errorMessage && errorMessage.toLowerCase().includes('file not found'))
  ) {
    const notFoundMessage =
      'File not found. The requested file may have been deleted or expired.'
    console.error(notFoundMessage)
    if (onError) onError(notFoundMessage)
    return
  }

  // Case 3: Other HTTP errors with detailed logging
  if (status && status !== 200) {
    const rawErrorData = error.error

    const httpErrorMessage = rawErrorData
      ? typeof rawErrorData === 'string'
        ? rawErrorData
        : JSON.stringify(rawErrorData)
      : `Failed to get presigned URL: ${status}`

    console.error(`HTTP ${status} error:`, {
      url: filename,
      errorMessage: httpErrorMessage,
    })

    if (onError) onError(httpErrorMessage)
    handleFallback(originalSource, filename)
    return
  }

  // Case 4: General error handling for other cases
  const finalErrorMessage = errorMessage || 'Failed to get presigned URL'
  if (onError) onError(finalErrorMessage)
  handleFallback(originalSource, filename)
}

/**
 * Helper type guard to check if an object has a specific property
 */
const hasProperty = <T extends object, K extends PropertyKey>(
  obj: T,
  prop: K
): obj is T & Record<K, unknown> => {
  return Object.prototype.hasOwnProperty.call(obj, prop)
}

/**
 * Extract error message from various error formats
 */
export const extractErrorMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined

  // Check for different error patterns using type guards
  if (
    hasProperty(err, 'error') &&
    err.error &&
    typeof err.error === 'object' &&
    err.error !== null &&
    hasProperty(err.error, 'message') &&
    typeof err.error.message === 'string'
  ) {
    return err.error.message
  }

  if (hasProperty(err, 'error') && typeof err.error === 'string') {
    return err.error
  }

  if (hasProperty(err, 'message') && typeof err.message === 'string') {
    return err.message
  }

  return undefined
}

/**
 * Transforms an S3 URL into a secure download URL for the application's API
 */
export const transformS3UrlToDownloadUrl = (
  url: string,
  gptWsBaseApi: string
): string => {
  const urlObject = new URL(url)
  const pathParts = urlObject.pathname.split('/')
  const isUserUploaded = urlObject.pathname.startsWith('/user-uploaded')
  
  // pathParts for user-uploaded = ["", "user-uploaded", "user", "dsUsername", "s3Filename"]
  // pathParts for cwic-generated = ["", "cwic-generated", "user", "userId", "s3Filename"]
  const s3Filename = pathParts[pathParts.length - 1]
  
  let dsUsername
  
  if (isUserUploaded) {
    dsUsername = pathParts[3]
  }
  
  const contentDisposition =
    urlObject.searchParams.get('response-content-disposition') || ''
  
  // Extract the filename from the content disposition header
  // Content disposition can be in two formats:
  // 1. URL-encoded: attachment%3B%20filename%3D%22top_5_non_cash_securities.csv%22
  // 2. Regular: attachment; filename="quarterly_report.csv"
  
  // First try the URL-encoded pattern
  let filenameMatch = contentDisposition.match(
    /filename%3D%22([^%"]+(?:%[^%"]+)*)%22/
  )
  
  // If no match, try the regular pattern
  if (!filenameMatch) {
    filenameMatch = contentDisposition.match(/filename="([^"]+)"/) 
  }
  
  // Extract filename from content disposition if available, otherwise use the S3 filename
  let display_filename = s3Filename
  if (filenameMatch && filenameMatch[1]) {
    display_filename = decodeURIComponent(filenameMatch[1])
  }
  
  // Use knowledge endpoint for user-uploaded documents, documents for other files
  return isUserUploaded
    ? `${gptWsBaseApi}/digital-specialist-ws/knowledge/${dsUsername}/${s3Filename}`
    : `${gptWsBaseApi}/documents/user/${s3Filename}?display_filename=${encodeURIComponent(
        display_filename
      )}`
}

/**
 * Checks if the new download endpoint is available
 */
export const checkDownloadEndpointStatus = async (
  httpClient: HttpClient,
  testUrl: string
): Promise<boolean> => {
  try {
    const token = await getToken()
    await httpClient
      .head(testUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .toPromise()

    return true // If we get here, the endpoint exists
  } catch (error) {
    console.error('Download endpoint not available:', error)
    return false
  }
}
